/**
 * Billing service: subscription and payment methods.
 * Uses backend API and optionally Stripe for secure card tokenization.
 */

import apiClient from './api';

const BILLING_BASE = '/billing';

let cachedSubscription = null;
let subscriptionFetchTime = 0;
let inFlightSubscriptionPromise = null;
const SUBSCRIPTION_CACHE_TTL_MS = 60 * 1000; // 60 seconds

export const invalidateSubscriptionCache = () => {
  cachedSubscription = null;
  subscriptionFetchTime = 0;
  inFlightSubscriptionPromise = null;
};

if (typeof window !== 'undefined') {
  window.addEventListener('subscriptionUpdated', () => {
    invalidateSubscriptionCache();
  });
}

const billingService = {
  /**
   * Get current org subscription with request deduplication and memory cache.
   * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh data.
   * @returns {Promise<{plan, billing_cycle, num_users, trial_ends_at, payment_completed_at, renewal_date, is_trial_active, trial_completed, is_paid, seats_used, seats_limit, seats_available}>}
   */
  getSubscription: async (forceRefresh = false) => {
    const now = Date.now();
    if (forceRefresh) {
      invalidateSubscriptionCache();
    } else if (cachedSubscription && (now - subscriptionFetchTime < SUBSCRIPTION_CACHE_TTL_MS)) {
      return cachedSubscription;
    }
    if (inFlightSubscriptionPromise) {
      return inFlightSubscriptionPromise;
    }
    const url = forceRefresh ? `${BILLING_BASE}/subscription/?refresh=true` : `${BILLING_BASE}/subscription/`;
    inFlightSubscriptionPromise = apiClient.get(url)
      .then((res) => {
        cachedSubscription = res.data;
        subscriptionFetchTime = Date.now();
        inFlightSubscriptionPromise = null;
        return res.data;
      })
      .catch((err) => {
        inFlightSubscriptionPromise = null;
        throw err;
      });
    return inFlightSubscriptionPromise;
  },

  /**
   * Force refresh storage and subscription usage across the application.
   * Dispatches window events immediately and schedules a follow-up sync.
   */
  refreshStorageUsage: async () => {
    invalidateSubscriptionCache();
    let data = null;
    try {
      data = await billingService.getSubscription(true);
    } catch (e) {
      data = null;
    }
    if (typeof window !== 'undefined') {
      if (data) {
        window.dispatchEvent(new CustomEvent('subscriptionUpdated', { detail: data }));
        if (data.storage_usage) {
          window.dispatchEvent(new CustomEvent('storageUsageUpdated', { detail: data.storage_usage }));
        }
      } else {
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
        window.dispatchEvent(new CustomEvent('storageUsageUpdated'));
      }

      setTimeout(() => {
        invalidateSubscriptionCache();
        billingService
          .getSubscription(true)
          .then((deferredSub) => {
            if (deferredSub) {
              window.dispatchEvent(new CustomEvent('subscriptionUpdated', { detail: deferredSub }));
              if (deferredSub.storage_usage) {
                window.dispatchEvent(new CustomEvent('storageUsageUpdated', { detail: deferredSub.storage_usage }));
              }
            }
          })
          .catch(() => {});
      }, 2500);
    }
    return data;
  },

  /**
   * Invalidate local subscription cache manually.
   */
  invalidateCache: invalidateSubscriptionCache,

  /**
   * Get lightweight trial countdown, milestone progress, and paywall state.
   */
  getTrialStatus: async () => {
    const res = await apiClient.get(`${BILLING_BASE}/trial-status/`);
    return res.data;
  },

  /**
   * Get cloud storage usage breakdown per employee and media category.
   * @param {boolean} forceRefresh - If true, bypass cache and fetch fresh calculation.
   * @returns {Promise<Object>}
   */
  getStorageBreakdown: async (forceRefresh = false) => {
    const res = await apiClient.get(`${BILLING_BASE}/storage-breakdown/${forceRefresh ? '?refresh=true' : ''}`);
    return res.data;
  },

  /**
   * Update subscription (plan, billing_cycle, num_users, trial_ends_at, payment_completed_at, renewal_date).
   */
  updateSubscription: async (data) => {
    invalidateSubscriptionCache();
    const res = await apiClient.put(`${BILLING_BASE}/subscription/`, data);
    cachedSubscription = res.data;
    subscriptionFetchTime = Date.now();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscriptionUpdated', { detail: res.data }));
    }
    return res.data;
  },

  /**
   * List payment methods (cards) for current org.
   * @returns {Promise<Array<{id, last4, brand, is_primary, created_at}>>}
   */
  getPaymentMethods: async () => {
    const res = await apiClient.get(`${BILLING_BASE}/payment-methods/`);
    return res.data;
  },

  /**
   * List real consumed checkout order invoices for the current org.
   * @returns {Promise<Array<{id, invoiceNumber, date, plan, users, amount, status, payment_id}>>}
   */
  getInvoices: async () => {
    try {
      const res = await apiClient.get(`${BILLING_BASE}/invoices/`);
      return Array.isArray(res.data?.invoices) ? res.data.invoices : [];
    } catch (err) {
      console.warn('[billingService] Failed to load invoices:', err);
      return [];
    }
  },

  /**
   * Add payment method. Use Stripe token/PM id when available; otherwise last4/brand for demo.
   * @param {Object} data - { last4, brand?, stripe_payment_method_id?, is_primary? }
   */
  addPaymentMethod: async (data) => {
    const res = await apiClient.post(`${BILLING_BASE}/payment-methods/`, data);
    return res.data;
  },

  /**
   * Set payment method as primary.
   */
  setPrimaryPaymentMethod: async (id) => {
    const res = await apiClient.patch(`${BILLING_BASE}/payment-methods/${id}/`, {
      is_primary: true,
    });
    return res.data;
  },

  /**
   * Remove payment method.
   */
  removePaymentMethod: async (id) => {
    await apiClient.delete(`${BILLING_BASE}/payment-methods/${id}/`);
  },

  /**
   * Validate a coupon code.
   */
  validateCoupon: async (code) => {
    const res = await apiClient.post(`${BILLING_BASE}/coupon/validate/`, { code });
    return res.data;
  },

  /**
   * Create Razorpay payment order for subscription upgrade.
   */
  createRazorpayOrder: async (data) => {
    const res = await apiClient.post(`${BILLING_BASE}/razorpay/create-order/`, data);
    return res.data;
  },

  /**
   * Verify Razorpay payment signature and activate paid subscription.
   */
  verifyRazorpayPayment: async (data) => {
    const res = await apiClient.post(`${BILLING_BASE}/razorpay/verify-payment/`, data);
    if (typeof window !== 'undefined' && res.data?.subscription) {
      window.dispatchEvent(new CustomEvent('subscriptionUpdated', { detail: res.data.subscription }));
    }
    return res.data;
  },
  /**
   * Super Admin: List all coupons.
   */
  getAdminCoupons: async () => {
    const res = await apiClient.get(`${BILLING_BASE}/admin/coupons/`);
    return res.data;
  },

  /**
   * Super Admin: Create a new single-use coupon.
   * @param {Object} data - { code?: string, discount_percentage: number }
   */
  createAdminCoupon: async (data) => {
    const res = await apiClient.post(`${BILLING_BASE}/admin/coupons/`, data);
    return res.data;
  },

  /**
   * Super Admin: Delete a coupon.
   */
  deleteAdminCoupon: async (id) => {
    await apiClient.delete(`${BILLING_BASE}/admin/coupons/${id}/`);
  },
};

export default billingService;

